import { XMarkIcon } from "@heroicons/react/24/outline";
import { CursorArrowRaysIcon } from "@heroicons/react/24/solid";
import { TooltipRenderProps } from "react-joyride";
import Button from "../components/common/Button";
import { TourStep } from "./types";

const DEFAULT_HINT = "Click the highlighted element to continue";

const TutorialTooltip: React.FC<TooltipRenderProps> = ({
  backProps,
  closeProps,
  continuous,
  index,
  isLastStep,
  primaryProps,
  size,
  skipProps,
  step,
  tooltipProps,
}) => {
  const showBack = index > 0;
  const showSkip = !isLastStep;
  // joyride hands back the merged step, custom fields included.
  const tourStep = step as TourStep;
  const waitsForAction = !!tourStep.advanceOn;

  return (
    <div
      {...tooltipProps}
      className="w-[420px] max-w-[90vw] bg-base-100 rounded-xl shadow-2xl border border-base-300 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-base-200 bg-base-200/80 select-none">
        <div className="flex items-center gap-3">
          {step.title && (
            <h4 className="text-sm font-semibold text-base-content m-0">
              {step.title}
            </h4>
          )}
          <span className="text-xs font-medium text-base-content/40 bg-base-200 px-2 py-0.5 rounded-full">
            {index + 1} of {size}
          </span>
        </div>
        <button
          {...closeProps}
          aria-label="Close tutorial"
          title="Close tutorial"
          className="text-base-content/40 hover:text-base-content/70 transition-colors"
        >
          <XMarkIcon className="size-5" />
        </button>
      </div>

      {/* Content */}
      <div className="px-5 py-4">
        <div className="text-sm text-base-content/80 leading-relaxed [&_p]:m-0 [&_p+p]:mt-2">
          {step.content}
        </div>
      </div>

      {/* Action hint: the step advances when the officer does the thing */}
      {waitsForAction && (
        <div className="flex items-center gap-2 px-5 pb-3 text-xs text-blue-600 dark:text-blue-300">
          <CursorArrowRaysIcon className="size-4 shrink-0" />
          <span>{tourStep.hint || DEFAULT_HINT}</span>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-base-200 bg-base-200/50">
        <div>
          {showSkip && (
            <button
              {...skipProps}
              className="text-xs text-base-content/40 hover:text-base-content/70"
            >
              Skip
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showBack && (
            <Button {...backProps} variant="outline" size="sm">
              Back
            </Button>
          )}
          {waitsForAction ? (
            <Button {...primaryProps} variant="outline" size="sm">
              Skip step
            </Button>
          ) : (
            <Button {...primaryProps} variant="primary" size="sm">
              {continuous ? (isLastStep ? "Done" : "Next") : "Close"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorialTooltip;
